/**
 * The host for OVL-016 surfaces: one worker per surface node, drawing the activity's own pixels.
 *
 * The platform composes a rectangle and hands it over. `openWorker` and `makeCanvas` are ports, so
 * this runs headless in tests.
 */

import type { Box, SurfaceSettings } from "../scene/sceneTree";

/** What the host sends a module. One way. */
export type SurfaceMessage =
  | {
      readonly kind: "start";
      readonly canvas: unknown;
      readonly budgetMs: number;
      readonly moduleUrl: string;
      /** What this surface's own declaration named, already resolved to values. CAT-023. */
      readonly settings: SurfaceSettings;
    }
  | { readonly kind: "resize"; readonly width: number; readonly height: number }
  | { readonly kind: "snapshot"; readonly entities: readonly unknown[]; readonly stale: boolean };

/** What a module's worker sends back. Never pixels. */
export type SurfaceReport =
  | { readonly kind: "ready" }
  | { readonly kind: "failed"; readonly reason: string }
  | { readonly kind: "frame"; readonly tookMs: number };

export interface SurfaceWorker {
  post(message: SurfaceMessage, transfer?: unknown[]): void;
  onReport(listen: (report: SurfaceReport) => void): void;
  terminate(): void;
}

export interface SurfaceCanvas {
  /** Position this canvas over the scene, in the normalized space a `Box` is in. */
  place(box: Box): void;
  /** Hand pixel ownership to the worker. Callable once. */
  detach(): unknown;
  readonly width: number;
  readonly height: number;
  remove(): void;
}

export interface SurfacePorts {
  moduleUrl(activityId: string, surface: string): string;
  openWorker(): SurfaceWorker;
  makeCanvas(): SurfaceCanvas;
  report(line: string): void;
}

export const DEFAULT_BUDGET_MS = 8;

/** Consecutive over-budget frames a surface is allowed before it is dropped. */
export const OVERRUN_FRAMES = 30;

interface Held {
  readonly worker: SurfaceWorker;
  readonly canvas: SurfaceCanvas;
  readonly activityId: string;
  readonly surface: string;
  /** What this surface's own declaration promised per frame. */
  readonly budgetMs: number;
  overruns: number;
  dropped: boolean;
  /** The size last sent to the worker, so an unchanged one is never resent. */
  width: number;
  height: number;
}

/** Every live surface, keyed by render node key. */
export class SurfaceLayer {
  private readonly held = new Map<string, Held>();
  /** Nodes this host cannot serve a module for, so one update does not report on every build. */
  private readonly refused = new Set<string>();

  constructor(private readonly ports: SurfacePorts) {}

  get live(): number {
    return [...this.held.values()].filter((one) => !one.dropped).length;
  }

  /** Start a surface for this node, or move the one already drawing it. */
  open(
    key: string,
    activityId: string,
    surface: string,
    box: Box,
    declaredBudgetMs?: number,
    settings: SurfaceSettings = {},
  ): void {
    if (this.held.has(key)) {
      this.place(key, box);
      return;
    }

    const budgetMs =
      declaredBudgetMs !== undefined && declaredBudgetMs > 0 ? declaredBudgetMs : DEFAULT_BUDGET_MS;
    const moduleUrl = this.ports.moduleUrl(activityId, surface);
    if (moduleUrl === "") {
      this.refuse(key, `surface "${surface}" of ${activityId} has no module url on this host`);
      return;
    }
    const canvas = this.ports.makeCanvas();
    const worker = this.ports.openWorker();
    const held: Held = {
      worker,
      canvas,
      activityId,
      surface,
      budgetMs,
      overruns: 0,
      dropped: false,
      width: 0,
      height: 0,
    };
    this.held.set(key, held);

    canvas.place(box);
    held.width = canvas.width;
    held.height = canvas.height;
    worker.onReport((report) => {
      this.take(key, report);
    });

    const transferred = canvas.detach();
    worker.post(
      {
        kind: "start",
        canvas: transferred,
        budgetMs,
        moduleUrl,
        settings,
      },
      [transferred],
    );
  }

  /** Move a surface already drawing, resizing it only when the size actually changed. */
  place(key: string, box: Box): void {
    const held = this.held.get(key);
    if (held === undefined || held.dropped) return;

    held.canvas.place(box);
    const { width, height } = held.canvas;
    if (width === held.width && height === held.height) return;

    held.width = width;
    held.height = height;
    held.worker.post({ kind: "resize", width, height });
  }

  /** Hand this build's entities to every live surface. A dropped one is skipped. */
  push(entities: readonly unknown[], stale: boolean): void {
    for (const held of this.held.values()) {
      if (held.dropped) continue;
      held.worker.post({ kind: "snapshot", entities, stale });
    }
  }

  /** Stop one surface. Safe to call twice. */
  close(key: string): void {
    const held = this.held.get(key);
    if (held === undefined) return;
    this.held.delete(key);
    held.worker.terminate();
    held.canvas.remove();
  }

  closeAll(): void {
    for (const key of [...this.held.keys()]) this.close(key);
  }

  /** Say once why this node gets no surface, and never open a worker that cannot import. */
  private refuse(key: string, why: string): void {
    if (this.refused.has(key)) return;
    this.refused.add(key);
    this.ports.report(`${why}, so it draws nothing`);
  }

  private take(key: string, report: SurfaceReport): void {
    const held = this.held.get(key);
    if (held === undefined || held.dropped) return;

    if (report.kind === "failed") {
      this.drop(held, key, report.reason);
      return;
    }
    if (report.kind !== "frame") return;

    held.overruns = report.tookMs > held.budgetMs ? held.overruns + 1 : 0;
    if (held.overruns >= OVERRUN_FRAMES) {
      const why = `over its ${String(held.budgetMs)}ms budget for ${String(OVERRUN_FRAMES)} frames`;
      this.drop(held, key, why);
    }
  }

  /** Take this surface's layer away, and nothing else on the canvas. */
  private drop(held: Held, key: string, why: string): void {
    held.dropped = true;
    this.ports.report(`surface "${held.surface}" of ${held.activityId} dropped: ${why}`);
    this.close(key);
  }
}
